import React from "react";
import ReactDOM from "react-dom/client";
import { ThemeProvider } from "./contexts/ThemeContext";
import "./i18n/config"; // Initialize i18n
import "./index.css";
import reportWebVitals from "./reportWebVitals";
import { getUserId } from "./utils/userIdManager";

const BASE_API_URL = process.env.REACT_APP_BASE_API_URL;

// Feature Flags API for embedding applications
// Allows embedding apps to set feature flags before session creation
window.widgetFeatureFlags = undefined;

window.setWidgetFeatureFlags = (flags: string[]) => {
  window.widgetFeatureFlags = flags;
};

window.getWidgetFeatureFlags = () => {
  return window.widgetFeatureFlags;
};

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

// ── Routing: chat (default) vs portal vs register vs chart ─────────────────────
// The widget bundle hosts multiple surfaces; the embedding page picks one via the
// `mode` URL query param (or `window.chatWidgetConfig.mode`):
//   - mode=chat (default) → chat UI (App.tsx)
//   - mode=portal         → Customer Portal shell (PortalShell)
//   - mode=register       → Self-service registration page
//   - mode=chart          → Full-screen chart overlay (ChartDetailSurface) — a
//                           lightweight, zero-network surface fed via postMessage.
// The heavy chat/portal/register tree is code-split into ./surfaces/ChatPortalApp
// so the chart overlay never downloads or evaluates SignalR / App / portal.
const params = new URLSearchParams(window.location.search);
const applicationToken = params.get("applicationToken") ?? "";
const widgetConfig = (window as any).chatWidgetConfig as
  | {
      requireCustomerAuth?: boolean;
      userId?: string;
      mode?: "chat" | "portal" | "register" | "chart";
      customerCompanyId?: string;
    }
  | undefined;
const jsFlagRequireAuth = widgetConfig?.requireCustomerAuth === true;
const mode =
  (params.get("mode") as "chat" | "portal" | "register" | "chart" | null) ??
  widgetConfig?.mode ??
  "chat";
const customerId = getUserId();
const registrationCustomerCompanyId =
  params.get("customerCompanyId") ?? widgetConfig?.customerCompanyId ?? "";

/**
 * Pre-fetch the per-widget configuration to learn whether the server requires
 * customer authentication for this widget. Falls back to the JS-config flag when
 * the call fails or the token is missing.
 */
async function resolveServerRequiresAuth(): Promise<{
  needsAuth: boolean;
  enforceCredential: boolean;
}> {
  if (!applicationToken || !BASE_API_URL)
    return { needsAuth: false, enforceCredential: false };
  try {
    const response = await fetch(
      `${BASE_API_URL.replace(
        /\/+$/g,
        ""
      )}/api/TokenValidation/validate/${applicationToken}?widgetSetup=true`,
      { method: "GET" }
    );
    if (!response.ok) return { needsAuth: false, enforceCredential: false };
    const data = await response.json();
    const requiresEndUserAuth = data?.requiresEndUserAuth === true;
    const portalEnabled = data?.portalEnabled === true;
    return {
      needsAuth: requiresEndUserAuth || portalEnabled,
      enforceCredential: requiresEndUserAuth,
    };
  } catch {
    return { needsAuth: false, enforceCredential: false };
  }
}

/**
 * For a widget that does NOT enforce end-user auth, decide whether an injected, pre-existing
 * user id must still sign in. If that id already has a saved credential in the DB, the server
 * refuses an anonymous session for it (POST /anon → 403 `requiresAuth`) and the widget would
 * otherwise fall back to a throwaway `anon-…` identity — stranding the user away from their own
 * history. We probe up front and, when the id is credentialed (or temporarily locked out), mount
 * the auth gate so they log in. An injected login ticket still auto-authenticates them inside the
 * gate (Mode A redemption runs before the login screen), so no prompt shows in that case.
 *
 * Returns false (→ stay anonymous) for unknown / no-credential ids, disabled accounts, and any
 * network/parse failure, so anonymous visitors are never blocked by a transient error.
 */
async function injectedUserHasSavedCredential(
  customerId: string
): Promise<boolean> {
  if (!applicationToken || !BASE_API_URL || !customerId) return false;
  try {
    const response = await fetch(
      `${BASE_API_URL.replace(
        /\/+$/g,
        ""
      )}/api/customer-auth/probe?customerId=${encodeURIComponent(
        customerId
      )}&widgetId=${encodeURIComponent(applicationToken)}`,
      { method: "GET" }
    );
    if (!response.ok) return false;
    const data = await response.json();
    // needs-entry = exists with credential; locked = credentialed + in lockout. Both mean a
    // credential is saved → show the auth screen. needs-setup / disabled do not force sign-in.
    return data?.screen === "needs-entry" || data?.screen === "locked";
  } catch {
    return false;
  }
}

(async () => {
  let tree: React.ReactNode;

  if (mode === "chart") {
    // Lightweight overlay surface — NO SignalR, NO auth, NO config fetch. Renders
    // purely from the chart payload the chat surface injects via postMessage.
    const { ChartDetailSurface } = await import(
      "./components/ChartDetailSurface"
    );
    tree = (
      <ThemeProvider>
        <ChartDetailSurface />
      </ThemeProvider>
    );
  } else {
    // ── Mode A silent sign-in ────────────────────────────────────────────────
    // Redeem an injected one-shot login ticket FIRST — before the auth gate or the SignalR
    // token acquisition read the session store. A ticket is the embedding tenant asserting who
    // this page load belongs to, so it outranks any session a previous visitor left behind in
    // this browser (the session store is keyed by widget, not by visitor). Doing it here rather
    // than inside the auth gate means it applies to every widget configuration, including public
    // widgets that never mount the gate. Failure is non-fatal: the visitor continues through the
    // normal login / anonymous flow, under their own identity.
    const { customerAuthService } = await import(
      "./services/customerAuthService"
    );
    await customerAuthService.redeemInjectedLoginTicket(applicationToken);

    // Portal mode always requires authentication; JS flag implies enforcement.
    // Chat mode resolves from the server.
    let needsAuth = mode === "portal" || jsFlagRequireAuth;
    let enforceCredential = mode === "portal" || jsFlagRequireAuth;
    if (mode === "chat" && !needsAuth) {
      const resolved = await resolveServerRequiresAuth();
      needsAuth = resolved.needsAuth;
      enforceCredential = resolved.enforceCredential;
    }

    // Global rule for chat widgets that don't already enforce credentials (public, or
    // portal-enabled-but-public): if the host injected a user id that already has a saved
    // credential, force sign-in instead of silently minting an anonymous session. An injected
    // login ticket auto-authenticates the user inside the gate, so no prompt shows in that case.
    if (mode === "chat" && !enforceCredential) {
      const injectedUserId =
        params.get("userId") ?? widgetConfig?.userId ?? null;
      if (
        injectedUserId &&
        (await injectedUserHasSavedCredential(injectedUserId))
      ) {
        needsAuth = true;
        enforceCredential = true;
      }
    }

    const { ChatPortalApp } = await import("./surfaces/ChatPortalApp");
    // ThemeProvider wraps EVERYTHING so the dark/light class on <body> is set
    // before any auth/registration UI paints.
    tree = (
      <ThemeProvider>
        <ChatPortalApp
          mode={mode}
          customerId={customerId}
          widgetId={applicationToken}
          registrationCustomerCompanyId={registrationCustomerCompanyId}
          needsAuth={needsAuth}
          enforceCredential={enforceCredential}
        />
      </ThemeProvider>
    );
  }

  root.render(<React.StrictMode>{tree}</React.StrictMode>);

  // Tell the host the app is live. StrikeUpChat.isReady() / onReady() are held until
  // this arrives, because the iframe element exists long before this line does — every
  // await above (ticket redemption, token validation, two code-split chunks) runs while
  // the document is still blank. Announced here rather than from a component so an
  // auth-gated widget, where <App/> mounts only after sign-in, still reports ready
  // promptly; anything open() staged in the meantime is replayed on <App/> mount.
  window.parent?.postMessage({ type: "strikeup:app:ready" }, "*");
})();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
